const LEADER_STORAGE_KEY = 'hridaya.chat.leader.v1';
const LEADER_CHANNEL_NAME = 'hridaya.chat.leader.channel.v1';
const LEASE_MS = 8000;
const HEARTBEAT_MS = 2000;

const safeNow = () => Date.now();

const makeTabId = () => {
  const rand = Math.random().toString(36).slice(2);
  return `tab_${safeNow()}_${rand}`;
};

class ChatTabRealtimeCoordinator {
  constructor() {
    this.tabId = makeTabId();
    this.started = false;
    this.isLeaderTab = false;
    this.listeners = new Set();
    this.heartbeatTimer = null;
    this.channel = null;

    this.onBroadcastMessage = this.onBroadcastMessage.bind(this);
    this.onStorage = this.onStorage.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBeforeUnload = this.onBeforeUnload.bind(this);
  }

  start() {
    if (this.started) return;
    this.started = true;

    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(LEADER_CHANNEL_NAME);
      this.channel.addEventListener('message', this.onBroadcastMessage);
    }

    window.addEventListener('storage', this.onStorage);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('focus', this.onFocus);
    window.addEventListener('beforeunload', this.onBeforeUnload);

    this.heartbeatTimer = window.setInterval(() => {
      if (this.isLeaderTab) {
        this.writeLease();
        this.broadcast({ type: 'heartbeat', tabId: this.tabId, ts: safeNow() });
        return;
      }

      if (!document.hidden && !this.hasValidForeignLeader()) {
        this.requestLeadership('heartbeat');
      }
    }, HEARTBEAT_MS);

    this.requestLeadership('start');
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.isLeaderTab);
    return () => {
      this.listeners.delete(listener);
    };
  }

  isLeader() {
    return this.isLeaderTab;
  }

  requestLeadership(reason = 'manual') {
    if (document.hidden) {
      this.setLeader(false);
      return false;
    }

    const lease = this.readLease();
    const leaseOwner = lease?.tabId;
    const leaseValid = this.isLeaseValid(lease);
    const canTakeover =
      !leaseValid || leaseOwner === this.tabId || reason === 'focus' || reason === 'start';

    if (!canTakeover) {
      this.setLeader(leaseOwner === this.tabId);
      return false;
    }

    this.writeLease();
    this.broadcast({ type: 'leader', tabId: this.tabId, ts: safeNow(), reason });
    this.setLeader(true);
    return true;
  }

  pauseInBackground() {
    if (!document.hidden) return;

    const lease = this.readLease();
    if (lease?.tabId === this.tabId) {
      this.clearLease();
      this.broadcast({ type: 'released', tabId: this.tabId, ts: safeNow() });
    }
    this.setLeader(false);
  }

  hasValidForeignLeader() {
    const lease = this.readLease();
    return this.isLeaseValid(lease) && lease.tabId !== this.tabId;
  }

  onBroadcastMessage(event) {
    const payload = event?.data || {};
    const {tabId} = payload;
    if (!tabId || tabId === this.tabId) return;

    if (payload.type === 'leader' || payload.type === 'heartbeat') {
      this.setLeader(false);
    }
  }

  onStorage(event) {
    if (event.key !== LEADER_STORAGE_KEY) return;
    const lease = this.readLease();
    const isMine = lease?.tabId === this.tabId && this.isLeaseValid(lease);
    this.setLeader(!!isMine);
  }

  onVisibility() {
    if (document.hidden) {
      this.pauseInBackground();
    } else {
      this.requestLeadership('focus');
    }
  }

  onFocus() {
    this.requestLeadership('focus');
  }

  onBeforeUnload() {
    const lease = this.readLease();
    if (lease?.tabId === this.tabId) {
      this.clearLease();
      this.broadcast({ type: 'released', tabId: this.tabId, ts: safeNow() });
    }
  }

  setLeader(next) {
    if (this.isLeaderTab === next) return;
    this.isLeaderTab = next;
    this.listeners.forEach((listener) => {
      try {
        listener(next);
      } catch (e) {
        console.log('ChatTabRealtimeCoordinator listener error', e);
      }
    });
  }

  readLease() {
    try {
      const raw = localStorage.getItem(LEADER_STORAGE_KEY);
      if (!raw) return null;
      const lease = JSON.parse(raw);
      if (!lease || !lease.tabId || !lease.ts) return null;
      return lease;
    } catch {
      return null;
    }
  }

  writeLease() {
    const lease = {
      tabId: this.tabId,
      ts: safeNow()
    };
    try {
      localStorage.setItem(LEADER_STORAGE_KEY, JSON.stringify(lease));
    } catch (e) {
      console.log('Failed to write chat leader lease', e);
    }
  }

  clearLease() {
    try {
      const lease = this.readLease();
      if (lease?.tabId === this.tabId) {
        localStorage.removeItem(LEADER_STORAGE_KEY);
      }
    } catch (e) {
      console.log('Failed to clear chat leader lease', e);
    }
  }

  isLeaseValid(lease) {
    if (!lease || !lease.ts) return false;
    return safeNow() - lease.ts < LEASE_MS;
  }

  broadcast(message) {
    try {
      this.channel?.postMessage(message);
    } catch (e) {
      console.log('Failed to broadcast chat leader event', e);
    }
  }
}

export const chatTabRealtimeCoordinator = new ChatTabRealtimeCoordinator();
